import { ADD_ROI_BEARER_TOKEN } from '../../store';
import { cookieGetToken, isCookieLoggedIn } from '../../reducers/cookies';
import axios from 'axios';
import { isLoading } from '../Loading/isLoading';
import { isError } from '../Loading/isError';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import apiEndpoint from '../apiEndpoint';

export const makeUrl = (state, endpoint, urlParams) =>
    `${apiEndpoint}${endpoint}${urlParams ? joinUrlParms(urlParams) : ''}`;

export const makePostRequest = (pathArray, urlParams, data) => axios(getAxiosPostRequest(pathArray, urlParams, data));
export const makePutRequest = (pathArray, urlParams, data) => axios(getAxiosPutRequest(pathArray, urlParams, data));
export const makeDeleteRequest = (pathArray, urlParams, data) =>
    axios(getAxiosDeleteRequest(pathArray, urlParams, data));

//https://stackoverflow.com/questions/35038884/download-file-from-bytes-in-javascript
export function base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const binaryLen = binaryString.length;
    let bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
        let ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
    }
    return bytes;
}

// same as Leasing Portal - needs to be moved to hooks package!
export function downloadFile(response, fileName, type) {
    const array = base64ToArrayBuffer(response);
    const a = document.createElement('a');
    a.style.display = 'none';
    document.body.appendChild(a);
    a.href = window.URL.createObjectURL(new Blob([array], { type }));
    a.setAttribute('download', fileName);
    a.click();
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
};

//supports add/update/delete
export const updateData = (data, rowKey, add, defaultAdd = true, remove = new Set(), ordinal = undefined) => {
    const addNotEdit = defaultAdd ? add.filter(f => !remove.has(f[rowKey]) && !ordinal) : [];
    let added = false;
    const updatedInOrder = data.reduce((acc, f) => {
        //insert in correct place
        if (!added && ordinal !== undefined && f.ordinal > ordinal) {
            acc.push(...add.filter(f => !remove.has(f[rowKey])));
            added = true;
        }

        //remove/edit else nothing
        if (remove.has(f[rowKey])) {
            let editedTerm = add.find(r => r[rowKey] === f[rowKey]);

            if (editedTerm) acc.push(editedTerm);
        } else acc.push(f);

        return acc;
    }, []);

    if (!added && ordinal !== undefined) updatedInOrder.push(...add);

    return [...updatedInOrder, ...addNotEdit];
};

export const getAxiosDeleteRequest = (pathArray, urlParams, data) => ({
    method: 'DELETE',
    url: makeUrl(undefined, makePath(pathArray), urlParams),
    headers: makeHeaders(),
    data
});
export const getAxiosPostRequest = (pathArray, urlParams, data) => ({
    method: 'POST',
    url: makeUrl(undefined, makePath(pathArray), urlParams),
    headers: makeHeaders(),
    data
});

export const getAxiosPutRequest = (pathArray, urlParams, data) => ({
    method: 'PUT',
    url: makeUrl(undefined, makePath(pathArray), urlParams),
    headers: makeHeaders(),
    data
});

export const getAxiosPostOfflineRequest = (pathArray, urlParams, data) => ({
    method: 'POST',
    url: makeUrl(undefined, makePath(pathArray), urlParams),
    headers: makeHeadersOffline(),
    data
});

export const makeGetRequest = (pathArray, urlParams = {}) => axios(getAxiosGetRequest(pathArray, urlParams));

export const getAxiosGetRequest = (pathArray, urlParams = {}) => ({
    method: 'GET',
    url: makeUrl(undefined, makePath(pathArray), urlParams),
    headers: makeHeaders()
});

export const getAxiosGetOfflineRequest = (pathArray, urlParams = {}) => ({
    method: 'GET',
    url: makeUrl(undefined, makePath(pathArray), urlParams),
    headers: makeHeaders()
});

export const makeHeaders = () => {
    const token = cookieGetToken();
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
        // 'Cache-Control': 'no-cache'
    };
};

export const getRestFormatDateTimeNow = () => moment().format(REST_TIME_FORMAT);

export const makeHeadersOffline = () => {
    return {
        'Content-Type': 'application/json',
        Authorization: ADD_ROI_BEARER_TOKEN
    };
};

export const REST_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';

export const makePath = parts =>
    parts.reduce((acc, part) => {
        if (part) {
            return `${acc}/${part}`;
        }
        return acc;
    }, '');

export const joinUrlParms = urlParams =>
    Object.entries(urlParams)
        .filter(([key, value]) => value !== undefined)
        .reduce((acc, [key, value]) => {
            if (Array.isArray(value)) {
                return [...acc, ...value.map(v => [key, v])];
            } else {
                acc.push([key, value]);
            }
            return acc;
        }, [])
        .reduce((acc, [key, value]) => `${acc}${acc === '' ? '?' : '&'}${key}=${encodeURIComponent(value)}`, '');

export const apiGet = (
    type,
    pathArray,
    urlParams = {},
    onResult = undefined,
    dispatchResultAsType = true,
    onError
) => async (dispatch, getState) => {
    if (abortRequest(getState)) {
        return false;
    }

    isLoading(dispatch, type, true, {});
    try {
        const result = await makeGetRequest(pathArray, urlParams);

        if (dispatchResultAsType) {
            dispatch({ type, data: result.data });
        }

        if (result.data && onResult) {
            dispatch(onResult(result));
        }

        isLoading(dispatch, type, false, {});
        return result || true;
    } catch (error) {
        handleError(dispatch, type, error, onError);
    }
};

//For when a get is called post due to passing data
export const apiGetPost = (
    type,
    pathArray,
    data,
    urlParams = {},
    onResult = undefined,
    dispatchResultAsType = true,
    onError
) => async (dispatch, getState) => {
    if (abortRequest(getState)) {
        return false;
    }

    isLoading(dispatch, type, true, {});
    try {
        const result = await makePostRequest(pathArray, urlParams, data);

        if (dispatchResultAsType) {
            dispatch({ type, data: result.data });
        }

        if (result.data && onResult) {
            dispatch(onResult(result));
        }

        isLoading(dispatch, type, false, {});
        return result || true;
    } catch (error) {
        handleError(dispatch, type, error, onError);
    }
};

export const abortRequest = getState => !isCookieLoggedIn() || !getState().offline.online;

export const apiRequest = ({
    httpMethod,
    type,
    pathArray,
    data = {},
    urlParams = {},
    onResult = undefined,
    dispatchResultAsType = true,
    onError = undefined,
    onResultWithDispatch = undefined
}) => async (dispatch, getState) => {
    if (abortRequest(getState)) {
        return false;
    }

    isLoading(dispatch, type, true, data);
    try {
        let result;
        switch (httpMethod) {
            case 'PUT':
                result = await makePutRequest(pathArray, urlParams, data);
                break;
            case 'POST':
                result = await makePostRequest(pathArray, urlParams, data);
                break;
            case 'DELETE':
                result = await makeDeleteRequest(pathArray, urlParams, data);
                break;
            case 'GET':
            default:
                result = await makeGetRequest(pathArray, urlParams, data);
                break;
        }

        if (dispatchResultAsType) {
            dispatch({ type, data: result.data });
        }

        if (result.data && onResult) {
            dispatch(onResult(result));
        }

        if (result.data && onResultWithDispatch) {
            onResultWithDispatch(dispatch, result);
        }

        isLoading(dispatch, type, false, {});
        return result || true;
    } catch (error) {
        handleError(dispatch, type, error, onError);
    }
};

export const apiPut = (
    type,
    pathArray,
    data,
    urlParams = {},
    onResult = undefined,
    dispatchResultAsType = true,
    onError
) => async (dispatch, getState) => {
    if (abortRequest(getState)) {
        return false;
    }

    isLoading(dispatch, type, true, {});
    try {
        const result = await makePutRequest(pathArray, urlParams, data);
        if (dispatchResultAsType) {
            dispatch({ type, data: result.data });
        }

        if (result.data && onResult) {
            dispatch(onResult(result));
        }
        return result || true;
    } catch (error) {
        handleError(dispatch, type, error, onError);
    }
};

const handleError = (dispatch, type, error, onError) => {
    if (onError) {
        return onError(dispatch, type, error.response ? error.response.data.error : error);
    } else {
        isError(dispatch, type, true, error.message, {});
        console.error(error);
        toastr.error(
            type,
            `Response ${error.response && error.response.data ? error.response.data.error.message : error.message}`
        );
        return false;
    }
};

export const apiDelete = (
    type,
    pathArray,
    data,
    urlParams = {},
    onResult = undefined,
    dispatchResultAsType = true,
    onError
) => async (dispatch, getState) => {
    if (abortRequest(getState)) {
        return false;
    }

    isLoading(dispatch, type, true, {});
    try {
        const result = await makeDeleteRequest(pathArray, urlParams, data);

        if (dispatchResultAsType) {
            dispatch({ type, data: result.data });
        }

        if (result.data && onResult) {
            dispatch(onResult(result));
        }

        isLoading(dispatch, type, false, {});
        return result || true;
    } catch (error) {
        handleError(dispatch, type, error, onError);
    }
};

export const offlinePost = (
    type,
    commit,
    rollback,
    modified,
    unmodified,
    data,
    id,
    pathArray,
    urlParams
) => dispatch => {
    const effect = getAxiosPostOfflineRequest(pathArray, urlParams, data);
    unmodified.error = undefined;
    data.createdDate = data.createdDate || getRestFormatDateTimeNow();
    data.updatedDate = getRestFormatDateTimeNow();
    dispatch({
        type,
        data: { ...modified, syncing: true },
        entityId: `${type}-${id}`, //Used in store.js to see if offline updates are targeting the same entity
        meta: {
            offline: {
                effect,
                commit: { type: commit, meta: { modified } },
                rollback: { type: rollback, meta: { unmodified, postedData: data } }
            }
        }
    });
};
